import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { usePathname } from 'next/navigation';

type PageContextType = {
  loadedPath: string | null;
  setIsLoaded: (path: string) => void;
};

const PageContext = createContext<PageContextType>({
  loadedPath: null,
  setIsLoaded: () => {},
});

export function PageContextProvider({ children }: { children: React.ReactNode }) {
  const [loadedPath, setLoadedPath] = useState<string | null>(null);

  const setIsLoaded = useCallback((path: string) => {
    setLoadedPath(prev => (prev === path ? prev : path));
  }, []);

  const value = useMemo(
    () => ({
      loadedPath,
      setIsLoaded,
    }),
    [loadedPath, setIsLoaded]
  );

  return <PageContext.Provider value={value}>{children}</PageContext.Provider>;
}

export function useReportPageLoaded(ready: boolean): void {
  const pathname = usePathname();
  const { setIsLoaded } = useContext(PageContext);

  useEffect(() => {
    if (ready && pathname) {
      setIsLoaded(pathname);
    }
  }, [ready, pathname, setIsLoaded]);
}

export function useIsCurrentPageLoaded(): boolean {
  const pathname = usePathname();
  const { loadedPath } = useContext(PageContext);
  return loadedPath !== null && loadedPath === pathname;
}
